import React, { useState } from 'react';
import { XCircle, UserPlus, Check } from 'lucide-react';
import ReusableDropdown from '../../../components/ReusableDropdown';

function AssignModal({
  onClose,
  onSubmit,
  loading,
  text,
  styles,
  spacing,
  fonts,
  allGroups,
  groups,
  userRole,
  availableEmployees,
}) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

  const groupList = (userRole === "admin" || userRole === "manager") ? allGroups : groups;

  const handleSubmit = () => {
    onSubmit(selectedGroup, selectedEmployeeIds, () => {
      setSelectedEmployeeIds([]);
      setSelectedGroup(null);
      onClose();
    });
  };

  const handleClose = () => {
    setSelectedEmployeeIds([]);
    onClose();
  };

  const toggleEmployee = (id) => {
    setSelectedEmployeeIds(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (!availableEmployees?.length) return;
    const allIds = availableEmployees.map(e => e.infinityCode);
    setSelectedEmployeeIds(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const allSelected = availableEmployees?.length > 0 && selectedEmployeeIds.length === availableEmployees.length;

  const focusStyle = (e) => {
    e.currentTarget.style.borderColor = "#2e7cba";
    e.currentTarget.style.boxShadow = "0 0 0 4px rgba(46, 124, 186, 0.15)";
  };
  const blurStyle = (e) => {
    e.currentTarget.style.borderColor = styles.formSelect?.borderColor || "rgba(46,124,186,0.2)";
    e.currentTarget.style.boxShadow = "none";
  };

  return (
    <div style={styles.modal} onClick={handleClose}>
      <div style={{ ...styles.modalContent, maxWidth: "560px" }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{text.assignEmployeesTitle}</h3>
          <button
            style={styles.closeButton}
            onClick={handleClose}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(46, 124, 186, 0.1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
          >
            <XCircle size={28} />
          </button>
        </div>

        {/* Select Group (ReusableDropdown) */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>
            {text.selectGroup} <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <ReusableDropdown
            items={groupList || []}
            value={selectedGroup?.groupId || ''}
            onChange={(id) => {
              const group = groupList.find(g => g.groupId === id) || null;
              setSelectedGroup(group);
            }}
            onSelect={(g) => setSelectedGroup(g)}
            getItemKey={(g) => g.groupId}
            getItemLabel={(g) => g.groupName}
            placeholder={text.chooseGroup}
            emptyMessage={text.noGroups || 'No groups'}
            maxItems={10}
            inputStyle={{ ...styles.formSelect }}
            dropdownStyle={{
              // force opaque background: strip alpha from rgba(...) if present
              background: (function() {
                const bg = styles.modalContent?.background || '#ffffff';
                try {
                  if (typeof bg === 'string' && bg.trim().startsWith('rgba')) {
                    return bg.replace(/rgba\(([^,]+),\s*([^,]+),\s*([^,]+),\s*([^\)]+)\)/, 'rgb($1,$2,$3)');
                  }
                } catch (e) { /* ignore */ }
                return bg;
              })(),
              border: styles.formSelect?.border || '1px solid rgba(0,0,0,0.1)',
              color: styles.formInput?.color || '#1a1a1a',
              boxShadow: 'none'
            }}
            itemStyle={{
              color: styles.formInput?.color || '#1a1a1a',
              borderBottom: '1px solid rgba(46,124,186,0.06)'
            }}
          />
        </div>

        {/* Select Employees — custom checkbox list */}
        <div style={styles.formGroup}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <label style={styles.formLabel}>
              {text.selectEmployees} <span style={{ color: "#ef4444" }}>*</span>
            </label>
            {availableEmployees?.length > 0 && (
              <button
                onClick={toggleAll}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: fonts.small || "0.78rem",
                  color: "#2e7cba",
                  fontWeight: "600",
                  padding: "0.2rem 0.5rem",
                }}
              >
                {allSelected ? text.deselectAll || "Deselect All" : text.selectAll || "Select All"}
              </button>
            )}
          </div>

          {/* Selected count badge */}
          {selectedEmployeeIds.length > 0 && (
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              background: "rgba(46, 124, 186, 0.12)",
              color: "#2e7cba",
              borderRadius: "999px",
              padding: "0.25rem 0.75rem",
              fontSize: fonts.small || "0.78rem",
              fontWeight: "600",
              marginBottom: "0.6rem",
            }}>
              <Check size={13} />
              {selectedEmployeeIds.length} {text.selected || "selected"}
            </div>
          )}

          {/* Scrollable employee list */}
          <div style={{
            border: "1.5px solid rgba(46, 124, 186, 0.2)",
            borderRadius: "0.75rem",
            overflow: "hidden",
            maxHeight: "220px",
            overflowY: "auto",
          }}>
            {availableEmployees && availableEmployees.length > 0 ? (
              availableEmployees.map((emp, idx) => {
                const isSelected = selectedEmployeeIds.includes(emp.infinityCode);
                return (
                  <div
                    key={emp.infinityCode}
                    onClick={() => toggleEmployee(emp.infinityCode)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.7rem 1rem",
                      cursor: "pointer",
                      background: isSelected
                        ? "rgba(46, 124, 186, 0.1)"
                        : idx % 2 === 0
                          ? "transparent"
                          : "rgba(46, 124, 186, 0.03)",
                      borderBottom: "1px solid rgba(46, 124, 186, 0.08)",
                      transition: "background 0.15s ease",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.background = "rgba(46, 124, 186, 0.06)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isSelected
                        ? "rgba(46, 124, 186, 0.1)"
                        : idx % 2 === 0 ? "transparent" : "rgba(46, 124, 186, 0.03)";
                    }}
                  >
                    {/* Custom checkbox */}
                    <div style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "5px",
                      border: isSelected ? "2px solid #2e7cba" : "2px solid rgba(46, 124, 186, 0.35)",
                      background: isSelected ? "#2e7cba" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s ease",
                    }}>
                      {isSelected && <Check size={11} color="white" strokeWidth={3} />}
                    </div>
                    <span style={{
                      fontSize: fonts.body,
                      color: isSelected ? "#2e7cba" : styles.formLabel?.color,
                      fontWeight: isSelected ? "600" : "400",
                    }}>
                      {emp.clientCode || emp.infinityCode} — {emp.sr}
                    </span>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: "1.5rem", textAlign: "center", color: "rgba(46,124,186,0.5)", fontSize: fonts.body }}>
                {text.loading}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={styles.modalActions}>
          <button style={styles.secondaryButton} onClick={handleClose}>
            {text.cancel}
          </button>
          <button style={styles.primaryButton} onClick={handleSubmit} disabled={loading}>
            <UserPlus size={20} />
            {loading ? text.loading : text.assign}
          </button>
        </div>

      </div>
    </div>
  );
}

export default AssignModal;